/**
 * SimplexNoise — 2D and 4D simplex noise with fbm() helpers.
 * 2D based on Stefan Gustavson's algorithm.
 * 4D based on Ashima Arts / Ian McEwan (same as looptile shaders).
 */

// Gradient vectors for 2D simplex noise
const GRAD2 = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [1, 0], [-1, 0], [0, 1], [0, -1]
];

const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;

// 4D simplex constants
const F4 = (Math.sqrt(5) - 1) / 4;       // 0.309016994374947451
const G4 = (5 - Math.sqrt(5)) / 20;       // 0.138196601125011
const G4x2 = G4 * 2;                      // 0.276393202250021
const G4x3 = G4 * 3;                      // 0.414589803375032
const G4x4m1 = G4 * 4 - 1;               // -0.447213595499958

const TAU = Math.PI * 2;

export default class SimplexNoise {
  constructor(seed = 0) {
    const perm = new Uint8Array(256);
    for (let i = 0; i < 256; i++) perm[i] = i;

    let s = seed | 0;
    for (let i = 255; i > 0; i--) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      const j = s % (i + 1);
      const tmp = perm[i];
      perm[i] = perm[j];
      perm[j] = tmp;
    }

    this._perm = new Uint8Array(512);
    this._permMod8 = new Uint8Array(512);
    for (let i = 0; i < 512; i++) {
      this._perm[i] = perm[i & 255];
      this._permMod8[i] = this._perm[i] & 7;
    }
  }

  // ── 2D simplex noise ──

  noise2D(x, y) {
    const perm = this._perm;
    const permMod8 = this._permMod8;

    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);

    const t = (i + j) * G2;
    const x0 = x - (i - t);
    const y0 = y - (j - t);

    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;

    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;

    const ii = i & 255;
    const jj = j & 255;

    let n0 = 0, n1 = 0, n2 = 0;

    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) {
      t0 *= t0;
      const g = GRAD2[permMod8[ii + perm[jj]]];
      n0 = t0 * t0 * (g[0] * x0 + g[1] * y0);
    }

    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) {
      t1 *= t1;
      const g = GRAD2[permMod8[ii + i1 + perm[jj + j1]]];
      n1 = t1 * t1 * (g[0] * x1 + g[1] * y1);
    }

    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) {
      t2 *= t2;
      const g = GRAD2[permMod8[ii + 1 + perm[jj + 1]]];
      n2 = t2 * t2 * (g[0] * x2 + g[1] * y2);
    }

    return 70 * (n0 + n1 + n2);
  }

  fbm(x, y, octaves = 4, lacunarity = 2.0, persistence = 0.5) {
    let value = 0, amplitude = 1, frequency = 1, maxAmplitude = 0;
    for (let i = 0; i < octaves; i++) {
      value += amplitude * this.noise2D(x * frequency, y * frequency);
      maxAmplitude += amplitude;
      amplitude *= persistence;
      frequency *= lacunarity;
    }
    return value / maxAmplitude;
  }

  // ── 4D simplex noise (Ashima Arts / Ian McEwan) ──

  /**
   * 4D simplex noise. Returns value in roughly [-1, 1].
   * Used for torus-mapped seamless tiling + temporal evolution.
   */
  noise4D(x, y, z, w) {
    const perm = this._perm;

    // Skew to 4D simplex cell
    const s = (x + y + z + w) * F4;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const k = Math.floor(z + s);
    const l = Math.floor(w + s);

    const t = (i + j + k + l) * G4;
    const x0 = x - (i - t);
    const y0 = y - (j - t);
    const z0 = z - (k - t);
    const w0 = w - (l - t);

    // Determine simplex traversal order by ranking components
    // rankX/Y/Z/W count how many other components each is greater than
    let rankX = 0, rankY = 0, rankZ = 0, rankW = 0;
    if (x0 > y0) rankX++; else rankY++;
    if (x0 > z0) rankX++; else rankZ++;
    if (x0 > w0) rankX++; else rankW++;
    if (y0 > z0) rankY++; else rankZ++;
    if (y0 > w0) rankY++; else rankW++;
    if (z0 > w0) rankZ++; else rankW++;

    // Simplex corner offsets (i1,j1,k1,l1) through (i3,j3,k3,l3)
    const i1 = rankX >= 3 ? 1 : 0, j1 = rankY >= 3 ? 1 : 0, k1 = rankZ >= 3 ? 1 : 0, l1 = rankW >= 3 ? 1 : 0;
    const i2 = rankX >= 2 ? 1 : 0, j2 = rankY >= 2 ? 1 : 0, k2 = rankZ >= 2 ? 1 : 0, l2 = rankW >= 2 ? 1 : 0;
    const i3 = rankX >= 1 ? 1 : 0, j3 = rankY >= 1 ? 1 : 0, k3 = rankZ >= 1 ? 1 : 0, l3 = rankW >= 1 ? 1 : 0;

    const x1 = x0 - i1 + G4, y1 = y0 - j1 + G4, z1 = z0 - k1 + G4, w1 = w0 - l1 + G4;
    const x2 = x0 - i2 + G4x2, y2 = y0 - j2 + G4x2, z2 = z0 - k2 + G4x2, w2 = w0 - l2 + G4x2;
    const x3 = x0 - i3 + G4x3, y3 = y0 - j3 + G4x3, z3 = z0 - k3 + G4x3, w3 = w0 - l3 + G4x3;
    const x4 = x0 + G4x4m1, y4 = y0 + G4x4m1, z4 = z0 + G4x4m1, w4 = w0 + G4x4m1;

    const ii = i & 255, jj = j & 255, kk = k & 255, ll = l & 255;

    // Hash-based gradient via permutation table
    const gi0 = perm[ii + perm[jj + perm[kk + perm[ll]]]] & 31;
    const gi1 = perm[ii + i1 + perm[jj + j1 + perm[kk + k1 + perm[ll + l1]]]] & 31;
    const gi2 = perm[ii + i2 + perm[jj + j2 + perm[kk + k2 + perm[ll + l2]]]] & 31;
    const gi3 = perm[ii + i3 + perm[jj + j3 + perm[kk + k3 + perm[ll + l3]]]] & 31;
    const gi4 = perm[ii + 1 + perm[jj + 1 + perm[kk + 1 + perm[ll + 1]]]] & 31;

    // 4D gradient table (32 gradients from 4D simplex corners)
    let n0 = 0, n1 = 0, n2 = 0, n3 = 0, n4 = 0;

    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0 - w0 * w0;
    if (t0 > 0) { t0 *= t0; n0 = t0 * t0 * _dot4(gi0, x0, y0, z0, w0); }

    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1 - w1 * w1;
    if (t1 > 0) { t1 *= t1; n1 = t1 * t1 * _dot4(gi1, x1, y1, z1, w1); }

    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2 - w2 * w2;
    if (t2 > 0) { t2 *= t2; n2 = t2 * t2 * _dot4(gi2, x2, y2, z2, w2); }

    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3 - w3 * w3;
    if (t3 > 0) { t3 *= t3; n3 = t3 * t3 * _dot4(gi3, x3, y3, z3, w3); }

    let t4 = 0.6 - x4 * x4 - y4 * y4 - z4 * z4 - w4 * w4;
    if (t4 > 0) { t4 *= t4; n4 = t4 * t4 * _dot4(gi4, x4, y4, z4, w4); }

    return 27.0 * (n0 + n1 + n2 + n3 + n4);
  }

  /**
   * Torus-mapped tiling noise (from looptile).
   * Maps 2D (u,v) + time onto a 4D torus, giving seamless spatial tiling
   * and smooth continuous temporal evolution.
   *
   * @param {number} u - Normalized tile X [0,1]
   * @param {number} v - Normalized tile Y [0,1]
   * @param {number} t - Time parameter (continuous, loops every 2π)
   * @param {number} radius - Spatial torus radius (controls feature scale)
   * @param {number} temporalRadius - Time circle radius (controls evolution intensity)
   * @param {number} phase - Phase offset (decorrelates octaves)
   */
  tilingNoise(u, v, t, radius, temporalRadius, phase) {
    const ax = u * TAU + phase;
    const ay = v * TAU + phase * 1.7;
    const at = t * TAU;
    return this.noise4D(
      Math.cos(ax) * radius,
      Math.sin(ax) * radius + Math.cos(at) * temporalRadius,
      Math.cos(ay) * radius,
      Math.sin(ay) * radius + Math.sin(at) * temporalRadius
    );
  }

  /**
   * FBM using torus-mapped tiling noise.
   * Each octave gets its own phase offset to decorrelate layers.
   */
  tilingFbm(u, v, t, baseRadius, temporalRadius, octaves = 4, lacunarity = 2.0, persistence = 0.5) {
    let value = 0, amplitude = 1, frequency = 1, maxAmplitude = 0;
    for (let i = 0; i < octaves; i++) {
      const radius = baseRadius * frequency;
      const phase = i * 2.3;
      value += amplitude * this.tilingNoise(u, v, t, radius, temporalRadius, phase);
      maxAmplitude += amplitude;
      amplitude *= persistence;
      frequency *= lacunarity;
    }
    return value / maxAmplitude;
  }
}

// 4D gradient table — 32 vectors pointing to edges of a 4D hypercube
const GRAD4 = [
  [0, 1, 1, 1], [0, 1, 1, -1], [0, 1, -1, 1], [0, 1, -1, -1],
  [0, -1, 1, 1], [0, -1, 1, -1], [0, -1, -1, 1], [0, -1, -1, -1],
  [1, 0, 1, 1], [1, 0, 1, -1], [1, 0, -1, 1], [1, 0, -1, -1],
  [-1, 0, 1, 1], [-1, 0, 1, -1], [-1, 0, -1, 1], [-1, 0, -1, -1],
  [1, 1, 0, 1], [1, 1, 0, -1], [1, -1, 0, 1], [1, -1, 0, -1],
  [-1, 1, 0, 1], [-1, 1, 0, -1], [-1, -1, 0, 1], [-1, -1, 0, -1],
  [1, 1, 1, 0], [1, 1, -1, 0], [1, -1, 1, 0], [1, -1, -1, 0],
  [-1, 1, 1, 0], [-1, 1, -1, 0], [-1, -1, 1, 0], [-1, -1, -1, 0]
];

function _dot4(gi, x, y, z, w) {
  const g = GRAD4[gi];
  return g[0] * x + g[1] * y + g[2] * z + g[3] * w;
}
